import { z } from 'zod';

import { ZodAmount } from '@Common/types/Amount';
import { ZodB2bSavingPlanCategory, ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import { ZodAmountInDays, ZodCampaignType } from '@Common/types/explore/employee_savings_campaign';
import { ZodFutureAllocationId } from '@Common/types/explore/future_allocations';
import {
  ZodInstrumentMutualFundAssetClass,
  ZodMutualFundValuationFrequency,
} from '@Common/types/instruments/instrument';
import { ZodInvestmentAccountTypeEnum } from '@Common/types/profile/me';
import { ZodProfileSavingPlanIdUriSafe } from '@Common/types/profile/saving_plans';
import { ZodTransferScope } from '@Common/types/profile/transactions/employee_savings_outgoing_transfer_transaction';
import {
  ZodLinkingType,
  ZodProfileActivityTransactionStatus,
  ZodProfileActivityTransactionType,
  ZodTransactionReferralOrigin,
  ZodTransactionReferralOriginBackend,
} from '@Common/types/profile/transactions/enums';
import { ZodGetTransactionPathParams } from '@Common/types/profile/transactions/transaction_path_params';
import { ZodCursorQuery, ZodId, ZodStringIDPathParam } from '@Common/types/request';
import { ZodCursorData } from '@Common/types/response';
import { ZodTransactionId } from '@Common/types/transactions';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { ZodOneEmoji } from '@Common/types/zod/zod-emoji';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { zodMaskedString } from '@Common/types/zod/zod-masked-string';
import { ZodIntegerLike } from '@Common/types/zod/zod-number-like';
import {
  createZodParamsValidator,
  createZodParamsValidatorWithEffects,
} from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodTimestampWithMicroseconds = ZodTrimmedString.length(26);

export const ZodGetProfileTransactionsQueryRefined = z.object({
  ...ZodCursorQuery.shape,
  cursor: ZodTrimmedString.optional(),
  account_type: ZodInvestmentAccountTypeEnum.default('gia'),
  saving_plan_ids: ZodProfileSavingPlanIdUriSafe.array().optional(),
  future_allocation_id: ZodFutureAllocationId.optional().describe(
    'Taiga only: lists the mandatory contributions allocated by this future allocation',
  ),
});

export const ZodGetProfileTransactionsQuery = createZodParamsValidatorWithEffects(
  ZodGetProfileTransactionsQueryRefined,
);

export type GetProfileTransactionsQuery = z.input<typeof ZodGetProfileTransactionsQuery>;

export const ZodProfileActivityTransaction = z.object({
  id: ZodTransactionId,
  order_id: z.number().nullable(),
  recurring_order_id: z.number().nullable(),
  entity_id: z.number().nullable(),
  company_name: ZodTrimmedString.nullable(),
  type: ZodProfileActivityTransactionType,
  emoji: ZodOneEmoji.nullable(),
  // date used to sort and as the cursor
  initiated_date: ZodTimestampWithMicroseconds,
  amount: ZodAmount.nullable(),
  percent: z.number().nullable(),
  status: ZodProfileActivityTransactionStatus,
  name: ZodTrimmedString.nullable(),
  logo_path: ZodTrimmedString.nullable(),
  instrument_id: z.number().nullable(),
  instrument_logos: ZodTrimmedString.array().nullable(),
  linking_type: ZodLinkingType.nullable(),
  referral_origin: ZodTransactionReferralOrigin.nullable(),
  is_free_stock: z.boolean().nullable(),
  is_matching_contribution: z.boolean().nullable(),
  is_managed_plan: z.boolean().nullable(),
  employee_savings_plan_type: ZodB2bSavingPlanType.nullable(),
  employee_savings_plan_category: ZodB2bSavingPlanCategory.nullable(),
  employee_savings_campaign_type: ZodCampaignType.nullable(),
  correlation_id: ZodTrimmedString.nullable(),
  employee_savings_transfer_scope: ZodTransferScope.nullable(),
  allocation_percent: z.number().nullable(),
  amount_in_days: ZodAmountInDays.nullable(),
  estimated_execution_date: ZodDateString.nullable(),
  has_cet_agreement: z.boolean().nullable(),
});

export type ProfileActivityTransaction = z.infer<typeof ZodProfileActivityTransaction>;

export const ZodProfileTaigaBreakdownTransaction = z.object({
  amount: ZodAmount,
  instrument_id: z.number(),
  instrument_logo: ZodTrimmedString.nullable(),
});

export type ProfileTaigaBreakdownTransaction = z.infer<typeof ZodProfileTaigaBreakdownTransaction>;

export const ZodProfileActivityTransactionBackend = z.object({
  ...ZodProfileActivityTransaction.omit({
    referral_origin: true,
  }).shape,
  referral_origin: ZodTransactionReferralOriginBackend.nullable(),
});

export type ProfileActivityTransactionBackend = z.infer<
  typeof ZodProfileActivityTransactionBackend
>;

export const ZodProfileActivityTransactionsResponse = z.object({
  transactions: z.array(ZodProfileActivityTransaction),
  cursor: ZodCursorData,
});
export type ProfileActivityTransactionsResponse = z.infer<
  typeof ZodProfileActivityTransactionsResponse
>;

export const ZodProfileActivityTransactionsResponseBackend = z.object({
  transactions: z.array(ZodProfileActivityTransactionBackend),
  cursor: ZodCursorData,
});
export type ProfileActivityTransactionsResponseBackend = z.infer<
  typeof ZodProfileActivityTransactionsResponseBackend
>;

export const getProfileTransactionsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetProfileTransactionsQuery,
  responseBody: ZodProfileActivityTransactionsResponse,
});

export type GetProfileTransactionsEndpoint = GetEndpointType<typeof getProfileTransactionsEndpoint>;

export const ZodHistoricalTransactionType = z.enum([
  'subscription',
  'scheduled_voluntary_contribution',
  'matching_contribution',
  'company_voluntary_contribution',
  'profit_sharing',
  'statutory_profit_sharing',
  'capital_increase',
  'interest',
  'value_sharing',
  'redemption',
  'annual_fee_deduction',
  'arbitrage',
  'dividends',
  'settlement',
  'corporate_action',
  'fund_merger',
  'imported',
  'outgoing_transfer',
  'legal_deadline',
]);

export type HistoricalTransactionType = z.infer<typeof ZodHistoricalTransactionType>;

export const ZodHistoricalTransactionFilterType = ZodHistoricalTransactionType.exclude([
  'scheduled_voluntary_contribution',
]);

export type HistoricalTransactionFilterType = z.infer<typeof ZodHistoricalTransactionFilterType>;

export const ZodHistoricalTransactionListItem = z.object({
  id: ZodTransactionId,
  created_at: ZodDateString,
  amount: ZodAmount,
  type: ZodHistoricalTransactionType,
});

export type HistoricalTransactionListItem = z.infer<typeof ZodHistoricalTransactionListItem>;

export const HISTORICAL_TRANSACTIONS_CUTOFF_YEAR = 2021;

export const getProfileHistoricalTransactionsEndpoint = endpoint({
  method: 'GET',
  queryParams: createZodParamsValidator(
    z.object({
      ...ZodCursorQuery.shape,
      transaction_type: ZodHistoricalTransactionFilterType.optional(),
      year: z.number().int().min(HISTORICAL_TRANSACTIONS_CUTOFF_YEAR).optional(),
    }),
  ),
  responseBody: z.object({
    items: ZodHistoricalTransactionListItem.array(),
    cursor: ZodCursorData,
  }),
});

export type GetProfileHistoricalTransactionsEndpoint = GetEndpointType<
  typeof getProfileHistoricalTransactionsEndpoint
>;

const ZodHistoricalTransactionDetails = ZodHistoricalTransactionListItem.and(
  z.object({
    quantity: z.number().nullable(),
    net_amount: ZodAmount.nullable(),
    late_payment_interest_amount: ZodAmount.nullable(),
    savings_product: ZodB2bSavingPlanType.nullable(),
    executed_at: ZodDateString.nullable(),
    paid_at: ZodDateString.nullable(),
    payment_method: zodMaskedString.nullable(),
  }),
);

export type HistoricalTransactionDetails = z.infer<typeof ZodHistoricalTransactionDetails>;

export const getProfileHistoricalTransactionsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: ZodHistoricalTransactionDetails,
});

export type GetProfileHistoricalTransactionsIdEndpoint = GetEndpointType<
  typeof getProfileHistoricalTransactionsIdEndpoint
>;

export const ZodHistoricalTransactionBreakdownItem = z.object({
  id: ZodTransactionId,
  date: ZodDateString,
  saving_plan_category: ZodB2bSavingPlanCategory.nullable(),
  quantity: z.number().nullable(),
  nav: ZodAmount.nullable(),
  net_amount: ZodAmount.nullable(),
  gross_amount: ZodAmount.nullable(),
  late_payment_interest_amount: ZodAmount.nullish(),
  name: ZodTrimmedString,
  side: z.enum(['buy', 'sell']),
  type: z.enum(['source', 'target', 'matching_contribution']).optional(),
});

export type HistoricalTransactionBreakdownItem = z.infer<
  typeof ZodHistoricalTransactionBreakdownItem
>;

export const getProfileHistoricalTransactionsIdBreakdownEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: z.object({
    items: ZodHistoricalTransactionBreakdownItem.array(),
  }),
});

export type GetProfileHistoricalTransactionsIdBreakdownEndpoint = GetEndpointType<
  typeof getProfileHistoricalTransactionsIdBreakdownEndpoint
>;

export const ZodHistoricalTransactionPaymentItem = z.discriminatedUnion('type', [
  z.object({
    id: ZodTransactionId,
    type: z.literal('bank_transfer'),
    created_at: ZodDateString,
    paid_at: ZodDateString,
    destination_account: zodMaskedString,
    amount: ZodAmount,
  }),
  z.object({
    id: ZodTransactionId,
    type: z.enum(['bank_transfer_issued', 'cheque_payment']),
    created_at: ZodDateString,
    paid_at: ZodDateString.nullable(),
    destination_account: zodMaskedString.nullable(),
    amount: ZodAmount,
  }),
]);

export type HistoricalTransactionPaymentItem = z.infer<typeof ZodHistoricalTransactionPaymentItem>;

export const getProfileHistoricalTransactionsIdPaymentsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodStringIDPathParam,
  responseBody: z.object({
    items: ZodHistoricalTransactionPaymentItem.array(),
  }),
});

export type GetProfileHistoricalTransactionsIdPaymentsEndpoint = GetEndpointType<
  typeof getProfileHistoricalTransactionsIdPaymentsEndpoint
>;

export const ZodGetProfileTransactionsDocumentsDocumentIdParams = createZodParamsValidator(
  z.object({
    documentId: ZodIntegerLike,
  }),
);

export const ProfileTransactionDocumentTypeValues = [
  'early_withdrawal_request',
  'withdrawal_national_identity',
  'employee_savings_early_withdrawal',
  'employee_savings_withdrawal',
  'voluntary_payment',
  'matching_contribution',
  'arbitrage',
  'fully_managed_portfolio_rebalancing',
  'employee_savings_campaign_contribution',
  'b2b_incoming_external_transfer',
  'b2b_outgoing_external_transfer',
  'dividend',
  'voluntary_payment_national_identity',
  'voluntary_payment_funding_source',
  'fully_managed_plan_contribution_national_identity',
  'fully_managed_plan_contribution_funding_source',
] as const;

export type ProfileTransactionDocumentType = (typeof ProfileTransactionDocumentTypeValues)[number];

export const ZodGetProfileTransactionsDocumentsDocumentIdQueryParams = createZodParamsValidator(
  z.object({
    type: z.enum(ProfileTransactionDocumentTypeValues),
  }),
);

export const getProfileTransactionsDocumentsDocumentIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetProfileTransactionsDocumentsDocumentIdParams,
  queryParams: ZodGetProfileTransactionsDocumentsDocumentIdQueryParams,
  responseBody: z.object({
    document_url: ZodTrimmedString.url(),
  }),
});

export type GetProfileTransactionsDocumentsDocumentIdEndpoint = GetEndpointType<
  typeof getProfileTransactionsDocumentsDocumentIdEndpoint
>;

const ZodWithdrawalTransactionDocument = z.object({
  entity_type: z.literal('withdrawal'),
  document_id: ZodId,
  document_name: ZodTrimmedString,
  document_type: z.enum(['early_withdrawal_request', 'proof_of_identity']),
});

const ZodVoluntaryPaymentTransactionDocument = z.object({
  entity_type: z.literal('voluntary_payment'),
  document_id: ZodId,
  document_name: ZodTrimmedString,
  document_type: z.enum(['funding_source', 'proof_of_identity']),
});

const ZodFullyManagedPlanContributionTransactionDocument = z.object({
  entity_type: z.literal('fully_managed_plan_contribution'),
  document_id: ZodId,
  document_name: ZodTrimmedString,
  document_type: z.enum(['funding_source', 'proof_of_identity']),
});

const ZodTransactionDocument = z.discriminatedUnion('entity_type', [
  ZodWithdrawalTransactionDocument,
  ZodVoluntaryPaymentTransactionDocument,
  ZodFullyManagedPlanContributionTransactionDocument,
]);

export const getProfileTransactionsIdDocumentsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetTransactionPathParams,
  queryParams: undefined,
  body: undefined,
  responseBody: z.object({
    documents: ZodTransactionDocument.array(),
  }),
  headers: undefined,
});

export type GetProfileTransactionsIdDocumentsEndpoint = GetEndpointType<
  typeof getProfileTransactionsIdDocumentsEndpoint
>;

const ZodValuationFrequencyBreakdown = z.object({
  amount: ZodAmount,
  instruments: z.array(
    z.object({
      amount: ZodAmount,
      id: ZodId,
      name: ZodTrimmedString,
      logo_path: ZodTrimmedString.nullable(),
      risk: z.number().int().min(0).max(7),
      asset_class: ZodInstrumentMutualFundAssetClass.or(z.literal('ccb')),
      pricing_frequency: ZodMutualFundValuationFrequency,
    }),
  ),
});

export type ValuationFrequencyBreakdown = z.infer<typeof ZodValuationFrequencyBreakdown>;

export const getProfileTransactionsIdBreakdownValuationFrequencyEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetTransactionPathParams,
  queryParams: undefined,
  body: undefined,
  responseBody: z.partialRecord(
    ZodMutualFundValuationFrequency,
    ZodValuationFrequencyBreakdown.optional(),
  ),
  headers: undefined,
});

export type GetProfileTransactionsIdBreakdownValuationFrequencyEndpoint = GetEndpointType<
  typeof getProfileTransactionsIdBreakdownValuationFrequencyEndpoint
>;
